import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import JwtService from "../../auth/services/jwtService";
import { useT } from "../../i18n";

const Login: React.FC = () => {
  const { login } = useAuth();
  const t = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err: any) {
      setError(t("auth.errors.loginFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white flex items-center justify-center min-h-screen relative font-sans">
      <div className="w-full max-w-md p-10 bg-white rounded-lg border border-gray-300 shadow-md relative z-10">
        <div className="flex flex-col items-center mb-10">
          <div className="relative mb-6">
            <img
              src={process.env.PUBLIC_URL + "/luxia.svg"}
              alt={t("auth.signin.logoAlt")}
              className="w-16 h-16 object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {t("auth.signin.title")}
          </h1>
          <p className="text-gray-500 text-sm font-medium">
            {t("auth.signin.subtitle")}
          </p>
        </div>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <label
              htmlFor="email"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              {t("auth.signin.emailLabel")}
            </label>
            <div className="relative">
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3.5 pl-12 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-500 transition-all duration-200 placeholder-gray-400"
                placeholder={t("auth.signin.emailPlaceholder")}
                required
              />
              <i className="fa-regular fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
            </div>
          </div>
          <div className="space-y-2">
            <label
              htmlFor="password"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              {t("auth.signin.passwordLabel")}
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3.5 pl-12 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-500 transition-all duration-200 placeholder-gray-400"
                placeholder="••••••••"
                required
              />
              <i className="fa-regular fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <button
                type="button"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
              >
                <i
                  className={
                    showPassword
                      ? "fa-regular fa-eye-slash"
                      : "fa-regular fa-eye"
                  }
                ></i>
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center">
              <input
                type="checkbox"
                className="w-4 h-4 text-gray-600 bg-gray-50 border-gray-300 rounded focus:ring-gray-500 focus:ring-2"
              />
              <span className="ml-2 text-gray-600">{t("auth.signin.rememberMe")}</span>
            </label>
            <span className="text-gray-600 hover:text-gray-700 cursor-pointer font-medium">
              {t("auth.signin.forgotPassword")}
            </span>
          </div>
          {error && <div className="text-red-500 text-sm">{error}</div>}
          <div className="pt-2">
            <button
              type="submit"
              className="w-full bg-gray-700 hover:bg-gray-800 text-white py-3.5 rounded-lg font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 shadow-sm"
              disabled={loading}
            >
              <span className="flex items-center justify-center">
                <i className="fa-solid fa-arrow-right-to-bracket mr-2"></i>
                {loading ? t("auth.signin.submitting") : t("auth.signin.submit")}
              </span>
            </button>
          </div>
        </form>
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-2">{t("auth.signin.newPartner")}</p>
            <span className="text-gray-600 hover:text-gray-700 font-semibold text-sm cursor-pointer">
              {t("auth.signin.contactUs")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
